<template>
  <div class="table-responsive content-table-shell">
    <div
      v-if="isLoading"
      class="loading-overlay d-flex align-items-center justify-content-center"
    >
      <span class="loader"></span>
    </div>

    <table class="table content-table">
      <colgroup>
        <col style="width: 1%" />
        <col />
        <col style="width: 1%" />
        <col style="width: 1%" />
        <col style="width: 1%" />
      </colgroup>

      <thead class="content-table-head">
        <tr>
          <th>
            <div class="table-head-title table-head-title--sortable" :title="formatDate(contents[0]?.updatedAt || '')">
              Дата
              <span class="sort-icon" @click.stop="onChangeSort('updatedAt')">
                <i v-if="sortBy === 'updatedAt'" :class="sortDir === 'asc' ? 'bi bi-arrow-up' : 'bi bi-arrow-down'"></i>
                <i v-else class="bi bi-arrow-down-up"></i>
              </span>
            </div>
          </th>

          <th>
            <div class="ellipsis table-head-title">Название</div>
          </th>

          <th>
            <slot name="status-header">
              <div class="ellipsis table-head-title">Статус</div>
            </slot>
          </th>

          <th>
            <div class="ellipsis table-head-title">Управление</div>
          </th>

          <th>
            <div class="ellipsis table-head-title">Публикация</div>
          </th>
        </tr>
      </thead>

      <tbody>
        <tr v-for="content in contents" :key="content.id" class="content-row">
          <td class="content-date-cell">
            <div class="content-date-text">
              {{ formatDate(content.updatedAt) }}
            </div>
          </td>

          <td class="content-title-cell">
            <div class="ellipsis content-title-text" :title="content.title">
              <a
                class="btn btn-sm btn-link p-0 title-link-btn"
                v-if="publicationLink(content)"
                :href="publicationLink(content)"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i class="bi bi-box-arrow-up-right"></i>
              </a>
              {{ content.title }}
            </div>
          </td>

          <td class="content-status-cell" :class="getStatusClass(content.status)">
            <div class="ellipsis content-status-pill" :title="content.status">
              {{ content.status }}
            </div>
          </td>

          <td class="content-actions-cell">
            <div class="actions-wrapper actions-wrapper--manage">
              <button
                type="button"
                class="btn btn-secondary btn-sm"
                @click="$emit('generate-content', content)"
                title="Генерация"
                :disabled="content.status === 'ARCHIVE'"
              >
                <i class="bi bi-arrow-repeat"></i>
                ГЕНЕРАЦИЯ
              </button>

              <button
                type="button"
                class="btn btn-primary btn-sm"
                @click="$emit('edit-content', content)"
                title="Редактировать"
                :disabled="content.status === 'ARCHIVE'"
              >
                <i class="bi bi-feather"></i>
              </button>

              <button
                type="button"
                class="btn btn-primary btn-sm"
                @click="$emit('view-content', content)"
                title="Предпросмотр"
                disabled
              >
                <i class="bi bi-eye"></i>
              </button>

              <button
                type="button"
                class="btn btn-primary btn-sm"
                @click="$emit('copy-content', content)"
                title="Сделать копию"
                :disabled="content.status === 'ARCHIVE' || content.status === 'CREATED'"
              >
                <i class="bi bi-copy"></i>
              </button>

              <button
                v-if="content.status !== 'ARCHIVE'"
                type="button"
                class="btn btn-info btn-sm text-white"
                @click="$emit('archive-content', content)"
                title="Архивировать"
                :disabled="content.status === 'CREATED'"
              >
                <i class="bi bi-archive"></i>
              </button>

              <button
                v-if="content.status === 'ARCHIVE'"
                type="button"
                class="btn btn-info btn-sm text-white"
                @click="$emit('archive-content', content)"
                title="Разархивировать"
                :disabled="content.status === 'CREATED'"
              >
                <i class="bi bi-archive-fill"></i>
              </button>

              <button
                type="button"
                class="btn btn-danger btn-sm text-white"
                @click="$emit('delete-content', content)"
                title="Удалить"
              >
                <i class="bi bi-trash"></i>
              </button>
            </div>
          </td>

          <td class="content-publish-cell">
            <span
              class="d-inline-block w-100"
              :title="!allowed() ? (publishAllowed.message || 'Публикация недоступна') : ''"
            >
              <div class="actions-wrapper actions-wrapper--publish" v-if="!isArchive(content)">
                <button
                  v-if="uiFlags(content).showPublish"
                  type="button"
                  class="btn btn-success btn-sm text-white"
                  @click="publishContent(content)"
                  :disabled="uiFlags(content).publishDisabled"
                  title="Опубликовать"
                >
                  <i class="bi bi-upload me-1"></i>
                  ОПУБЛИКОВАТЬ
                </button>

                <button
                  v-if="uiFlags(content).showUpdate"
                  type="button"
                  class="btn btn-info btn-sm text-white"
                  @click="updateContent(content)"
                  :disabled="uiFlags(content).updateDisabled"
                  title="Обновить"
                >
                  <span class="font-monospace">ОБНОВИТЬ</span>
                </button>

                <button
                  v-if="uiFlags(content).showDelete"
                  type="button"
                  class="btn btn-danger btn-sm text-white"
                  @click="deleteContent(content)"
                  :disabled="uiFlags(content).deleteDisabled"
                  title="Стереть"
                >
                  <span class="font-monospace">СТЕРЕТЬ</span>
                </button>
              </div>

              <div v-else class="text-muted small">—</div>
            </span>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'ContentTable',
  props: {
    contents: { type: Array, required: true },
    sortBy: { type: String, default: 'updatedAt' },
    sortDir: { type: String, default: 'desc' },
    publishAllowed: {
      type: Object,
      default: () => ({ ok: true, valid: true, message: '' }),
    },
    streamType: { type: String, required: true },
    chatId: { type: String, default: '' },
    siteUrl: { type: String, default: '' },
    host: { type: String, default: '' },
  },
  data() {
    return {
      isLoading: false,
    }
  },
  methods: {
    isPublished(content) {
      return !!String(content?.externalId || '').trim()
    },
    allowed() {
      const pa = this.publishAllowed
      if (pa == null) return true
      if (typeof pa === 'boolean') return pa
      if (Object.prototype.hasOwnProperty.call(pa, 'ok')) return !!pa.ok
      if (Object.prototype.hasOwnProperty.call(pa, 'valid')) return !!pa.valid
      return true
    },
    onChangeSort(field) {
      this.$emit('change-sort', field)
    },
    formatDate(date) {
      if (!date) return ''
      return new Date(date).toLocaleDateString()
    },
    getStatusClass(status) {
      const map = {
        CREATED: 'text-secondary',
        DRAFT: 'text-primary',
        READY: 'text-info',
        PUBLISHED: 'text-success',
        ERROR: 'text-danger',
        ARCHIVE: 'text-warning',
      }
      return map[String(status || '').toUpperCase()] || ''
    },
    publishContent(content) {
      this.isLoading = true
      axios.post('/api/publish', null, {
        params: { streamId: content.streamId, contentId: content.id },
      })
        .then(r => {
          this.$emit('publish-success', r.data)
          window.location.reload()
        })
        .catch(e => {
          this.$emit('publish-error', e)
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    updateContent(content) {
      this.isLoading = true
      axios.put('/api/publish/edit', null, {
        params: { streamId: content.streamId, contentId: content.id },
      })
        .then(r => {
          this.$emit('update-success', r.data)
          window.location.reload()
        })
        .catch(e => {
          this.$emit('update-error', e)
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    deleteContent(content) {
      this.isLoading = true
      axios.delete('/api/publish/delete', {
        params: { streamId: content.streamId, contentId: content.id },
      })
        .then(r => {
          this.$emit('delete-success', r.data)
          window.location.reload()
        })
        .catch(e => {
          this.$emit('delete-error', e)
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    isTelegram() {
      return this.streamType === 'TELEGRAM'
    },
    isWordpress() {
      return this.streamType === 'WORDPRESS'
    },
    telegramLink(content) {
      if (!this.isTelegram()) return null
      const msgId = String(content?.externalId || '').trim()
      let id = String(this.chatId || '').trim()
      if (!msgId || !id) return null

      if (id.startsWith('@')) id = id.slice(1)
      if (/^-?\d+$/.test(id)) {
        const clean = id.replace(/^-100/, '').replace(/^-/, '')
        return `https://t.me/c/${clean}/${msgId}`
      }
      return `https://t.me/${id}/${msgId}`
    },
    wordpressLink(content) {
      if (!this.isWordpress()) return null
      const extId = String(content?.externalId || '').trim()
      const slug = String(content?.slug || '').trim().replace(/^\/+|\/+$/g, '')
      let base = String(this.host || this.siteUrl || '').trim()
      if (!extId || !slug || !base) return null

      if (!/^https?:\/\//i.test(base)) base = `https://${base}`
      try {
        const normalized = base.endsWith('/') ? base : `${base}/`
        return new URL(slug, normalized).toString()
      } catch (_) {
        return null
      }
    },
    publicationLink(content) {
      return this.telegramLink(content) || this.wordpressLink(content)
    },
    isArchive(content) {
      return String(content?.status || '').toUpperCase() === 'ARCHIVE'
    },
    uiFlags(content) {
      const st = String(content?.status || '').toUpperCase()
      const published = this.isPublished(content)
      const allowed = this.allowed()

      let showPublish = false
      let showUpdate = false
      let showDelete = false
      let publishDisabled = true
      let updateDisabled = true
      let deleteDisabled = true

      switch (st) {
        case 'CREATED':
          showPublish = true
          publishDisabled = true
          break
        case 'DRAFT':
          if (!published) {
            showPublish = true
            publishDisabled = true
          } else {
            showUpdate = true
            updateDisabled = true
            showDelete = true
            deleteDisabled = false
          }
          break
        case 'READY':
          if (!published) {
            showPublish = true
            publishDisabled = false
          } else {
            showUpdate = true
            updateDisabled = false
            showDelete = true
            deleteDisabled = false
          }
          break
        case 'ERROR':
          if (!published) {
            showPublish = true
            publishDisabled = false
          } else {
            showUpdate = true
            updateDisabled = false
            showDelete = true
            deleteDisabled = false
          }
          break
        case 'PUBLISHED':
          showUpdate = true
          updateDisabled = true
          showDelete = true
          deleteDisabled = false
          break
        default:
          showPublish = true
          publishDisabled = true
      }

      if (!allowed) {
        if (showPublish) publishDisabled = true
        if (showUpdate) updateDisabled = true
        if (showDelete) deleteDisabled = true
      }

      return { showPublish, showUpdate, showDelete, publishDisabled, updateDisabled, deleteDisabled }
    },
  },
}
</script>

<style scoped>
.content-table-shell {
  position: relative;
  overflow-y: visible !important;
  overflow-x: auto !important;
}

.content-table {
  table-layout: auto;
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  margin-bottom: 0;
}

.content-table-head th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: linear-gradient(180deg, #f6f9ff 0%, #f1f6ff 100%);
  border-bottom: 1px solid #d9e6fb;
  border-top: 0;
  padding: 10px 10px;
}

.content-table th,
.content-table td {
  border: 1px solid #e5ecfa;
  vertical-align: middle;
  background: #fff;
}

.content-table tbody tr td {
  padding: 7px 10px;
}

.content-row:nth-child(2n) td {
  background: #fcfdff;
}

.content-row:hover td {
  background: #f5f9ff;
}

.table-head-title {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #4b6188;
}

.table-head-title--sortable {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}

.ellipsis {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.2;
}

.content-date-text {
  font-size: 12px;
  font-weight: 600;
  color: #42597f;
  white-space: nowrap;
}

.content-title-text {
  font-size: 14px;
  color: #1f2f4f;
  font-weight: 600;
}

.title-link-btn {
  margin-right: 6px;
  color: #4872bc;
  vertical-align: baseline;
}

.content-status-cell {
  text-align: center;
  white-space: nowrap;
}

.content-actions-cell,
.content-publish-cell {
  width: 1%;
  white-space: nowrap;
}

.content-publish-cell {
  text-align: center;
}

.content-date-cell {
  width: 1%;
  white-space: nowrap;
}

.content-title-cell {
  min-width: 220px;
}

.content-status-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid currentColor;
  border-radius: 999px;
  padding: 2px 10px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: rgba(255, 255, 255, 0.7);
}

.sort-icon {
  margin-left: 0.5rem;
  cursor: pointer;
}

.actions-wrapper {
  display: flex;
  gap: 0.3rem;
}

.actions-wrapper--manage {
  flex-wrap: wrap;
  width: max-content;
  max-width: 100%;
}

.actions-wrapper--publish {
  flex-wrap: wrap;
  width: max-content;
  max-width: 100%;
  justify-content: center;
  margin-left: auto;
  margin-right: auto;
}

.actions-wrapper .btn {
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
  padding: 0.2rem 0.45rem;
  white-space: nowrap;
}

.actions-wrapper .btn[disabled] {
  opacity: 0.35;
}

.content-actions-cell .btn {
  min-height: 28px;
}

.loading-overlay {
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(9, 15, 30, 0.38);
  z-index: 2000;
  overflow: hidden;
}

.loader {
  width: 48px;
  height: 48px;
  border: 3px solid #fff;
  border-bottom-color: transparent;
  border-radius: 50%;
  display: inline-block;
  position: relative;
  box-sizing: border-box;
  animation: rotation 1s linear infinite;
}

.loader::after {
  content: '';
  position: absolute;
  box-sizing: border-box;
  left: 20px;
  top: 31px;
  border: 10px solid transparent;
  border-right-color: #fff;
  transform: rotate(-40deg);
}

@keyframes rotation {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@media (max-width: 1200px) {
  .content-table {
    min-width: 860px;
  }
}
</style>
